import { RadioButton } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { ButtonLoader } from '@va/svg-visa-icons';
import { fontWeights, Paragraph } from '@va/ui/design-system';
import { addNumberSeparator, getNumberWithArithmeticOperator, getPercentageChange } from '@va/util/helpers';
import classNames from 'classnames';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { COLORS_MAP, WebsiteActivityData } from './apiClient';
import { WebsiteChartDataset } from './VisitorsChart';

type Option = {
  label: string;
  value: WebsiteChartDataset;
};

type OverviewProps = {
  inProgress: boolean;
  setHiddenChartDatasets: Dispatch<SetStateAction<WebsiteChartDataset[]>>;
  hiddenChartDatasets: WebsiteChartDataset[];
  currentData: WebsiteActivityData | undefined;
  previousData: WebsiteActivityData | undefined;
};

const Overview = ({
  inProgress,
  setHiddenChartDatasets,
  hiddenChartDatasets,
  currentData,
  previousData,
}: OverviewProps) => {
  const translate = useTranslate();

  const handleOnClick = useCallback(
    (value: WebsiteChartDataset) => {
      setHiddenChartDatasets((prev) => {
        if (prev.includes(value)) return prev.filter((v) => v !== value);

        return [...prev, value];
      });
    },
    [setHiddenChartDatasets],
  );

  const option = useMemo<Option[]>(
    () => [
      {
        label: translate('all.manageWebsites.kpi.visits'),
        value: WebsiteChartDataset.Visits,
      },
      {
        label: translate('all.manageWebsites.kpi.sessions'),
        value: WebsiteChartDataset.Sessions,
      },
      {
        label: translate('all.manageWebsites.kpi.newVisitors'),
        value: WebsiteChartDataset.NewVisitors,
      },
      {
        label: translate('all.manageWebsites.kpi.visitors'),
        value: WebsiteChartDataset.Visitors,
      },
      {
        label: translate('all.manageWebsites.kpi.convertedSessions'),
        value: WebsiteChartDataset.ConvertedSessions,
      },
    ],
    [translate],
  );

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4u'>
      {option.map((item) => {
        const { label, value } = item;
        const total = currentData?.total[value];
        const prevTotal = previousData?.total[value];

        const isVisible = !hiddenChartDatasets.includes(value);

        return (
          <OverviewItem
            key={value}
            label={label}
            count={total}
            isSelected={isVisible}
            onClick={() => handleOnClick(value)}
            percentage={getPercentageChange(prevTotal, total)}
            inProgress={inProgress}
            optionValue={value}
          />
        );
      })}
    </div>
  );
};

export default Overview;

type OverviewItemProps = {
  label: string;
  count?: number;
  optionValue: WebsiteChartDataset;
  onClick: () => void;
  percentage?: number;
  inProgress: boolean;
  isSelected: boolean;
};

const OverviewItem = ({
  label,
  count = 0,
  onClick,
  percentage = 0,
  inProgress,
  isSelected,
  optionValue,
}: OverviewItemProps) => {
  const { locale } = useLocale();

  function getPercentageContent() {
    if (inProgress) return <ButtonLoader color={window.PRIMARY_COLOR} />;

    return (
      <Paragraph
        variant='interface'
        variantSize='large'
        weight={fontWeights.semibold}
        colorClassName={classNames({
          'text-green-green': percentage > 0,
          'text-red': percentage < 0,
          'text-gray-dusty': percentage === 0,
        })}
      >
        {`${isNaN(percentage) ? 0 : addNumberSeparator(getNumberWithArithmeticOperator(percentage), locale)}%`}
      </Paragraph>
    );
  }

  return (
    <div
      className={classNames('py-1 px-6u flex items-center rounded-4u hover:bg-white-snow', {
        'cursor-pointer': !inProgress,
      })}
      onClick={onClick}
    >
      <div className={classNames('flex items-center mr-4 cursor-pointer sm:hidden')}>
        <RadioButton isSelected={isSelected} color={COLORS_MAP[optionValue]} />
      </div>
      <div className='grow'>
        <Paragraph
          variant='interface'
          variantSize='large'
          weight={fontWeights.semibold}
          colorClassName='text-gray-dusty'
        >
          {label}
        </Paragraph>
        <div className='flex justify-between items-center'>
          <Paragraph weight={fontWeights.semibold}>{addNumberSeparator(count, locale)}</Paragraph>
          {getPercentageContent()}
        </div>
      </div>
    </div>
  );
};
